import React, { useEffect, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import Icon from 'react-native-vector-icons/Ionicons';

import { colors, font, space, radius } from '@/theme/chalkCourt';
import {
  isValidTime24,
  parseTimeInput,
  stepQuarterHour,
  to12Hour,
} from '@/utils/timeParser';

type Period = 'AM' | 'PM';

interface Props {
  label: string;
  /** 24-hour "HH:MM" (what the API takes), or '' when the typed text isn't a time. */
  value: string;
  onChange: (time24: string) => void;
  /** AM/PM shown while the field is empty (e.g. PM for a forgotten clock-out). */
  defaultPeriod?: Period;
  /**
   * Where − / + start on an empty field (24-hour "HH:MM"): the first tap fills
   * this time, later taps step by quarter hours. Defaults to 9:00 / 5:00 by
   * `defaultPeriod`.
   */
  emptyStart?: string;
}

const HAS_PERIOD_RE = /(a|am|p|pm)$/i;

/** "13:05" → { text: "1:05", period: "PM" }. */
const split12 = (time24: string): { text: string; period: Period } => {
  const [h, m] = time24.split(':').map((n) => parseInt(n, 10));
  const { hour, period } = to12Hour(h);
  return { text: `${hour}:${m.toString().padStart(2, '0')}`, period };
};

/**
 * Typed text → 24-hour time. An am/pm suffix wins; otherwise the AM/PM toggle
 * applies; 24-hour input ("14:30") still works on its own.
 */
const resolve = (text: string, period: Period): { time24: string; period: Period } | null => {
  const trimmed = text.trim();
  if (!trimmed) return null;
  if (!HAS_PERIOD_RE.test(trimmed.replace(/\s+/g, ''))) {
    const withToggle = parseTimeInput(`${trimmed}${period.toLowerCase()}`);
    if (withToggle.valid) return { time24: withToggle.time24, period: withToggle.period };
  }
  const p = parseTimeInput(trimmed);
  return p.valid ? { time24: p.time24, period: p.period } : null;
};

/**
 * 12-hour time entry: type the time ("9:58", "958", "4p"), flip AM/PM, or
 * nudge by quarter hours with − / +. Reports 24-hour "HH:MM" to the parent.
 */
const TimeField: React.FC<Props> = ({
  label,
  value,
  onChange,
  defaultPeriod = 'AM',
  emptyStart,
}) => {
  const initial = isValidTime24(value) ? split12(value) : { text: '', period: defaultPeriod };
  const [text, setText] = useState(initial.text);
  const [period, setPeriod] = useState<Period>(initial.period);

  // Follow outside changes (sheet re-seeded, stepper) without fighting typing.
  useEffect(() => {
    if (!isValidTime24(value)) return;
    if (resolve(text, period)?.time24 === value) return;
    const next = split12(value);
    setText(next.text);
    setPeriod(next.period);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const commit = (nextText: string, nextPeriod: Period) => {
    const r = resolve(nextText, nextPeriod);
    onChange(r ? r.time24 : '');
    return r;
  };

  const onChangeText = (t: string) => {
    setText(t);
    commit(t, period);
  };

  // Tidy what was typed ("958p" → "9:58" + PM) once they leave the field.
  const onBlur = () => {
    const r = resolve(text, period);
    if (r) {
      const next = split12(r.time24);
      setText(next.text);
      setPeriod(next.period);
    }
  };

  const pickPeriod = (p: Period) => {
    setPeriod(p);
    // Re-read the typed hour under the new half of the day (ignores any typed suffix).
    commit(text.replace(/\s*(a|am|p|pm)$/i, ''), p);
  };

  const step = (dir: 1 | -1) => {
    if (isValidTime24(value)) {
      onChange(stepQuarterHour(value, dir));
      return;
    }
    // Empty (or unreadable) field: fill a starting time rather than doing nothing.
    const start = emptyStart && isValidTime24(emptyStart)
      ? emptyStart
      : defaultPeriod === 'PM' ? '17:00' : '09:00';
    onChange(start);
  };

  const invalid = text.trim() !== '' && !isValidTime24(value);

  return (
    <View style={styles.wrap}>
      <Text style={styles.label}>{label}</Text>
      <View style={styles.row}>
        <TouchableOpacity
          style={styles.stepBtn}
          activeOpacity={0.7}
          accessibilityLabel={`${label} 15 minutes earlier`}
          onPress={() => step(-1)}>
          <Icon name="remove" size={20} color={colors.inkSoft} />
        </TouchableOpacity>
        <TextInput
          style={[styles.input, invalid && styles.inputInvalid]}
          value={text}
          onChangeText={onChangeText}
          onBlur={onBlur}
          placeholder="Type a time"
          placeholderTextColor={colors.inkFaint}
          autoCapitalize="none"
          autoCorrect={false}
          accessibilityLabel={`${label} time`}
        />
        <View style={styles.periods}>
          {(['AM', 'PM'] as Period[]).map((p) => (
            <TouchableOpacity
              key={p}
              style={[styles.periodBtn, period === p && styles.periodBtnOn]}
              activeOpacity={0.8}
              accessibilityLabel={`${label} ${p}`}
              accessibilityState={{ selected: period === p }}
              onPress={() => pickPeriod(p)}>
              <Text style={[styles.periodText, period === p && styles.periodTextOn]}>{p}</Text>
            </TouchableOpacity>
          ))}
        </View>
        <TouchableOpacity
          style={styles.stepBtn}
          activeOpacity={0.7}
          accessibilityLabel={`${label} 15 minutes later`}
          onPress={() => step(1)}>
          <Icon name="add" size={20} color={colors.inkSoft} />
        </TouchableOpacity>
      </View>
      {invalid && <Text style={styles.hint}>Enter a time like 9:30 or 4:15p</Text>}
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: {
    marginTop: space.md,
  },
  label: {
    fontFamily: font.bodyBold,
    fontSize: 11,
    letterSpacing: 1.6,
    color: colors.inkFaint,
    marginBottom: space.xs,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.xs,
  },
  stepBtn: {
    width: 44,
    height: 48,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.line,
    backgroundColor: colors.surface,
    alignItems: 'center',
    justifyContent: 'center',
  },
  input: {
    flex: 1,
    minWidth: 0,
    height: 48,
    backgroundColor: colors.surface,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.line,
    paddingHorizontal: space.md,
    fontFamily: font.bodyMed,
    fontSize: 18,
    color: colors.ink,
    textAlign: 'center',
  },
  inputInvalid: {
    borderColor: colors.accent,
  },
  periods: {
    flexDirection: 'row',
    height: 48,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.line,
    backgroundColor: colors.surfaceSunk,
    padding: 3,
    gap: 3,
  },
  periodBtn: {
    paddingHorizontal: 10,
    borderRadius: radius.sm,
    alignItems: 'center',
    justifyContent: 'center',
  },
  periodBtnOn: {
    backgroundColor: colors.ink,
  },
  periodText: {
    fontFamily: font.bodyBold,
    fontSize: 13,
    letterSpacing: 0.5,
    color: colors.inkSoft,
  },
  periodTextOn: {
    color: colors.surface,
  },
  hint: {
    fontFamily: font.body,
    fontSize: 12,
    color: colors.accentDark,
    marginTop: 4,
  },
});

export default TimeField;
